import { isAxiosError } from "axios"
import { AuthRepository } from "@/repositories/auth-repository"
import { User } from "@/models/user.model"
import { getToken } from "@clerk/vue"
import { isProductionEnvironment } from "@/helper/app/browser-helper"

const DEV_CURRENT_USER_RETRY_DELAY_MS = 3001
const DEV_CURRENT_USER_MAX_ATTEMPTS = 10

// PNTS-12313: a self-issued token (see authentication/views/impersonation.py)
// that overrides the normal Clerk token inside an impersonation column. The
// planning page runs the impersonated app in a same-origin iframe. Iframes
// share sessionStorage with their page, so the token is stored under a key
// scoped to that iframe's name, and only a document running inside that
// iframe ever reads it. The superuser's own page keeps its Clerk session.
export const IMPERSONATION_TOKEN_KEY = "impersonationToken"
export const IMPERSONATION_FRAME_PREFIX = "impersonation:"

function isTransientCurrentUserError(error: unknown): boolean {
  if (!isAxiosError(error)) {
    return true
  }
  if (!error.response) {
    return true
  }
  return [408, 429, 502, 503, 504].includes(error.response.status)
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export class AuthController {
  private authRepository: AuthRepository = new AuthRepository()

  public async acceptInvite(invitationCode: string): Promise<User | undefined> {
    const result = await this.authRepository.acceptInvite(invitationCode)
    if (result.status === 200) {
      return new User(result.data)
    } else if (result.status === 404) {
      throw Error("De uitnodigingscode is ongeldig of verlopen.")
    } else if (result.status === 409) {
      throw Error(
        "De uitnodiging kon niet worden geaccepteerd omdat er al een account is geregistreerd met deze credentials."
      )
    } else {
      throw Error(
        "Er ging iets mis bij het accepteren van de uitnodiging. Probeer het later opnieuw."
      )
    }
  }

  public async getDjangoUser(): Promise<User | null> {
    // In local/dev, retry while the backend is still starting so the loading
    // screen (awaiting initialize → current-user) stays up instead of flashing empty.
    const maxAttempts = isProductionEnvironment() ? 1 : DEV_CURRENT_USER_MAX_ATTEMPTS

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await this.authRepository.getDjangoUser()
      } catch (error) {
        const shouldRetry =
          !isProductionEnvironment() && attempt < maxAttempts && isTransientCurrentUserError(error)

        if (!shouldRetry) {
          console.log(error)
          return null
        }

        await sleep(DEV_CURRENT_USER_RETRY_DELAY_MS)
      }
    }

    return null
  }

  public static async getAccessToken(): Promise<string | null> {
    const impersonationToken = AuthController.getImpersonationToken()
    if (impersonationToken) {
      return `Bearer ${impersonationToken}`
    }

    try {
      let token = await getToken({ template: "jwt" })
      if (token && this._isExpiringSoon(token)) {
        token = await getToken({ template: "jwt", skipCache: true })
      }
      if (token) {
        const secure = isProductionEnvironment() ? "Secure;" : ""
        document.cookie = `clerk_jwt=${token}; path=/; ${secure} SameSite=Strict; max-age=3600`
        return `Bearer ${token}`
      }
      console.log("No active session found")
      return null
    } catch (e) {
      console.log("Failed to get token from Clerk", e)
      return null
    }
  }

  private static _isExpiringSoon(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]))
      return payload.exp * 1000 - Date.now() < 60_000
    } catch {
      return true
    }
  }

  public static async validateInviteCode(invitationCode: string): Promise<boolean> {
    const result = await new AuthRepository().validateInviteCode(invitationCode)
    return result.status === 200
  }

  public async startImpersonation(userId: string): Promise<string> {
    return this.authRepository.startImpersonation(userId)
  }

  public static getImpersonationFrameName(columnId: string): string {
    return IMPERSONATION_FRAME_PREFIX + columnId
  }

  private static impersonationTokenKey(frameName: string): string {
    return `${IMPERSONATION_TOKEN_KEY}:${frameName}`
  }

  private static isImpersonationFrame(): boolean {
    return window.self !== window.top && window.name.startsWith(IMPERSONATION_FRAME_PREFIX)
  }

  /** The token for the calling document. It only exists inside an impersonation iframe. */
  public static getImpersonationToken(): string | null {
    if (!AuthController.isImpersonationFrame()) {
      return null
    }
    return sessionStorage.getItem(AuthController.impersonationTokenKey(window.name))
  }

  public static hasImpersonationToken(frameName: string): boolean {
    return sessionStorage.getItem(AuthController.impersonationTokenKey(frameName)) !== null
  }

  public static setImpersonationToken(frameName: string, token: string): void {
    sessionStorage.setItem(AuthController.impersonationTokenKey(frameName), token)
  }

  public static clearImpersonationToken(frameName: string): void {
    sessionStorage.removeItem(AuthController.impersonationTokenKey(frameName))
  }

  /** When a frame's token expires, in ms since epoch, or null when there is no readable token. */
  public static getImpersonationTokenExpiresAt(frameName: string): number | null {
    const token = sessionStorage.getItem(AuthController.impersonationTokenKey(frameName))
    if (!token) {
      return null
    }
    try {
      const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")
      const { exp } = JSON.parse(atob(payload))
      return typeof exp === "number" ? exp * 1000 : null
    } catch {
      return null
    }
  }

  /**
   * Call on boot of the top-level page. Impersonation columns are never persisted,
   * so any token still in this tab's sessionStorage belongs to a column that is gone.
   */
  public static clearOrphanedImpersonationTokens(): void {
    if (window.self !== window.top) {
      return
    }
    for (const key of Object.keys(sessionStorage)) {
      if (key === IMPERSONATION_TOKEN_KEY || key.startsWith(`${IMPERSONATION_TOKEN_KEY}:`)) {
        sessionStorage.removeItem(key)
      }
    }
  }
}
